<template lang="pug">
	.cameraContainer
		b-loading(:is-full-page="true", :active.sync="isLoading")
		//- Debug: Show Stream Image
		div(:class="{ 'is-hidden': !debug }")
			img#streamImage
			canvas#streamOverlay.coveringCanvas(style="")

		//- Stream Video Preview
		video#streamPreview(autoplay)

		//- Start/Stop Buttons
		.buttons
			button.button(@click.prevent="startFaceRecognition", :disabled="isLoading") Start
			button.button(@click.prevent="stopFaceRecognition", :disabled="isLoading") Stop
		//- HandTrack Component
		HandTrack(ref="handTrack", :handTrackConfig="handTrackConfig", @handDetection="(detected) => handDetected = detected")
		br
		//- button.button(@click.prevent="getMediaStream") start media stream
		//- button.button(@click.prevent="takePhoto") take photo
		//- button.button(@click.prevent="stopMediaStream") stop
</template>

<style lang="scss" scoped>
	.coveringCanvas {
		margin-left: 20px; // DEPENDENT ON BUEFY STYLING (e.g. padding-left)
		position: absolute;
		left: 0;
	}

	#streamImage,
	#streamOverlay,
	#streamPreview {
		// https://stackoverflow.com/questions/14455844/is-that-possible-to-make-video-mirrored
		transform: rotateY(180deg);
		-webkit-transform: rotateY(180deg);
		-moz-transform: rotateY(180deg);
	}
</style>

<script>
	import {} from "vuex";
	import apiService from "@/services/apiService.js";

	// Face Recognition
	import * as faceapi from "face-api.js";
	const WEIGHTS_URL = "/faceApiWeights";

	let videoStream, // Webcam
		imageCapture, // Webcam (for taking a snapshot)
		faceRecognitionInterval; // Interval that continuously runs to process image thru face & hand recognition
	const defaultScoreThreshold = 0.4; // Threshold for Face API

	export default {
		props: ["org"],
		components: {
			HandTrack: () => import("@/components/HandTrack.vue")
		},
		data() {
			return {
				debug: false, // Show detection box around face
				isLoading: true, // True if Face API weights or Handtrack model is loading (only true in beginning)
				localApiLoading: false, // If Face API/Handtrack is loading, then don't run the analysis during the interval
				azureApiLoading: false, // If Azure API is loading, then don't call the API during the analysis
				imgBlob: null, // The image blob sent to Azure for processing
				handTrackConfig: { width: null, height: null, src: "" }, // Sent to HandTrack component, used to analyze the webcam image
				handDetected: false // True if hand detected, vice versa. Set by the HandTrack component
			};
		},
		computed: {},
		methods: {
			// Setup Face API.js Models
			async setupFaceAsync(callback) {
				// Load Face API Models First
				// await faceapi.loadSsdMobilenetv1Model(WEIGHTS_URL);
				// await faceapi.loadFaceLandmarkModel(WEIGHTS_URL);

				await faceapi.loadTinyFaceDetectorModel(WEIGHTS_URL);
				// await faceapi.loadFaceLandmarkTinyModel(WEIGHTS_URL);
				// await faceapi.loadFaceRecognitionModel(WEIGHTS_URL);

				// Setup Reference Faces
				// await this.setupReferenceImages();

				// Finally, return to callback
				callback();
			},
			// Turn on camera
			getMediaStream() {
				// https://stackoverflow.com/questions/33975431/how-can-i-capture-an-image-via-the-users-webcam-using-getusermedia
				// https://gist.github.com/anantn/1852070
				const video = document.getElementById("streamPreview");
				window.navigator.mediaDevices
					.getUserMedia({ video: true, audio: false })
					.then((mediaStream) => {
						// Store to global vars used in other methods
						videoStream = mediaStream;
						let mediaStreamTrack = mediaStream.getVideoTracks()[0];
						imageCapture = new ImageCapture(mediaStreamTrack);

						this.handTrackConfig.width = mediaStream.getTracks()[0].getSettings().width;
						this.handTrackConfig.height = mediaStream.getTracks()[0].getSettings().height;

						// Display video preview
						video.srcObject = mediaStream;
					})
					.catch((err) => console.log(err));
			},
			// Turn off camera
			stopMediaStream() {
				videoStream &&
					videoStream.getTracks().forEach((track) => {
						track.stop();
					});
			},
			// Take a webcam snapshot
			takePhoto() {
				return new Promise((resolve, reject) => {
					const img = document.getElementById("streamImage"),
						overlay = document.getElementById("streamOverlay");

					// Clear standard HTML canvas
					overlay.getContext("2d").clearRect(0, 0, overlay.width, overlay.height);

					// Take the photo and display on the stream image preview
					imageCapture
						.takePhoto()
						.then((blob) => {
							this.imgBlob = blob;
							img.src = window.URL.createObjectURL(blob);
							this.handTrackConfig.src = window.URL.createObjectURL(blob);
							resolve();
						})
						.catch((err) => reject(err));
				});
			},
			// Process Image thru Local Face API (Detect Face Quickly)
			async analyzeStreamImage() {
				if (this.isLoading) return;

				this.localApiLoading = true;

				// Take a webcam snapshot, then process it
				this.takePhoto()
					.then(async () => {
						const streamInput = document.getElementById("streamImage");

						// Detect a Single Face
						const streamDetection = await faceapi.detectSingleFace(
							streamInput,
							new faceapi.TinyFaceDetectorOptions({ scoreThreshold: defaultScoreThreshold })
						);
						// .withFaceLandmarks(true)
						// .withFaceDescriptor(true);
						// .withFaceLandmarks()
						// .withFaceDescriptor();

						if (!streamDetection) {
							// No face detected, so reset face-matched data in NewMeasurement component (send empty faceMatchedData)
							this.$emit("cameraSetEmployeeTag", {});
							this.localApiLoading = false;
							return;
						}

						if (this.debug) {
							// Draw detection box
							// resize the overlay canvas to the input dimensions
							const streamDisplaySize = { width: streamInput.width, height: streamInput.height };
							const streamCanvas = document.getElementById("streamOverlay");
							faceapi.matchDimensions(streamCanvas, streamDisplaySize);
							const streamResizedDetection = faceapi.resizeResults(streamDetection, streamDisplaySize);
							faceapi.draw.drawDetections(streamCanvas, streamResizedDetection);
							// faceapi.draw.drawFaceLandmarks(streamCanvas, streamResizedDetection);
						}

						// Send to Azure
						if (!this.azureApiLoading) {
							this.sendImageToAzure();
						}

						// Detect Hand
						await this.$refs.handTrack.track();

						this.localApiLoading = false;
					})
					.catch((err) => console.log(err));
			},
			// Process Image thru Microsoft Azure (Identify Face from Azure Database)
			async sendImageToAzure() {
				this.azureApiLoading = true;

				// A application/form-data that includes the organization (used for personGroupId) and image (sent to Azure as octet-stream)
				let formData = new FormData();
				formData.append("org", this.org);
				formData.append("imageFile", this.imgBlob);

				// Call API Service
				await apiService
					.recognizeFace(formData)
					.then((res) => {
						this.$emit("cameraSetEmployeeTag", { ...res.data, handDetected: this.handDetected });
						this.azureApiLoading = false;
					})
					.catch((err) => {
						console.log(err, err.response.data);

						this.$buefy.snackbar.open({
							duration: 5000,
							message: "Error: " + err.response.data.message,
							type: "is-danger",
							position: "is-top-right",
							queue: true
						});
						this.azureApiLoading = false;
					});
			},
			// Setup Handtrack and Start Camera
			async startFaceRecognition() {
				this.isLoading = true;

				// Setup Handtrack.js
				await this.$refs.handTrack.setup();
				// Start camera
				await this.getMediaStream();

				this.isLoading = false;

				// Continuously detect face
				setTimeout(() => {
					faceRecognitionInterval = setInterval(() => {
						if (!this.localApiLoading) {
							this.analyzeStreamImage();
						}
					}, 500);
				}, 1000);
			},
			// Stop Camera
			stopFaceRecognition() {
				// Stop camera and clear canvas
				this.stopMediaStream();
				const overlay = document.getElementById("streamOverlay");
				overlay.getContext("2d").clearRect(0, 0, overlay.width, overlay.height);

				// Stop continuous face recognition interval
				clearInterval(faceRecognitionInterval);
				faceRecognitionInterval = null;
			}
		},
		mounted() {
			// Setup Face API Models
			this.setupFaceAsync(() => {
				this.isLoading = false;

				// Start camera
				// this.getMediaStream();
			});
		},
		beforeDestroy() {
			// Stop continuous face recognition interval
			clearInterval(faceRecognitionInterval);
			faceRecognitionInterval = null;

			// Stop camera
			this.stopMediaStream();
		}
	};
</script>
